import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';

/**
 * Real 404 — and, on retired URLs, a real 410.
 *
 * The app previously routed every unmatched URL to `redirectTo: ''` — a broken link silently became
 * a 200 homepage. That is a soft 404 (spec §7 explicitly forbids it) and it lets Google index
 * unlimited junk URLs as duplicates of the homepage. This renders a proper not-found page AND tells
 * the SSR server to return the right status.
 *
 * Routes for URLs that were deliberately retired set `data: { gone: true }`. Those answer **410
 * Gone** rather than 404: 410 states the removal is permanent, which is what drops them from the
 * index fastest. They used to redirect to their replacements; a redirect keeps the old URL alive as
 * a ranking signal, which is the opposite of retiring it.
 */
@Component({
  selector: 'app-not-found-page',
  imports: [RouterLink],
  templateUrl: './not-found-page.html',
  styleUrl: './not-found-page.scss',
})
export class NotFoundPage {
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);

  /** True on a retired URL (route data `gone`), false on an ordinary unmatched one. */
  protected readonly isGone = computed(() => this.route.snapshot.data['gone'] === true);

  /**
   * True when we are only here because a request failed, not because the URL is unknown. The page
   * is reached this way for any one-segment slug whose `/resolve/` call did not come back, so it
   * must not tell the reader — or a crawler reading the copy — that the page does not exist.
   */
  protected readonly isUnavailable = signal(false);

  constructor() {
    const gone = this.route.snapshot.data['gone'] === true;
    // Read before setNotFound consumes the flag.
    const unavailable = !gone && this.ssrResponse.isTransientFailure();
    this.isUnavailable.set(unavailable);

    if (gone) {
      // A retired URL is retired whatever the API is doing, so this stays a hard 410.
      this.ssrResponse.setGone();
    } else {
      // Emits 503 when the primary fetch failed transiently, 404 when the slug is genuinely unknown.
      this.ssrResponse.setNotFound();
    }

    this.seo.apply({
      title: gone
        ? 'Page removed | SavingArena'
        : unavailable
          ? 'Temporarily unavailable | SavingArena'
          : 'Page not found | SavingArena',
      description: gone
        ? 'This page has been removed and is no longer available.'
        : unavailable
          ? 'This page could not be loaded right now. Please try again shortly.'
          : 'The page you are looking for does not exist or has been moved.',
      noindex: true,
      // No canonical on a dead URL: a self-referencing one would nominate the address as the
      // preferred version of itself while the status says it is gone.
      noCanonical: true,
    });
    this.seo.setJsonLd([]);
  }
}
